<template>
  <div class="agreements-container">
    <AppSectionHero
      title="All Agreements"
      subtitle="View and manage all available MOU agreements."
      :stats="heroStats"
      :show-refresh="false"
      @stat-click="selectStatusCard"
    />

    <CCard class="border-0 shadow-sm filter-shell">
      <CCardBody class="p-3 p-lg-4">
        <div class="search-box">
          <span class="search-icon">🔍</span>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search agreements by title, partner, department, type, or ID..."
          >
        </div>
      </CCardBody>
    </CCard>

    <CCard class="border-0 shadow-sm table-container">
      <div class="table-header">
        <span class="result-count">Showing {{ paginatedAgreements.length }} of {{ filteredAgreements.length }} agreements</span>
      </div>

      <CCardBody class="p-0">
        <table class="agreements-table">
          <thead>
            <tr>
              <th class="sortable" @click="sortBy('id')">
                ID <span class="sort-icon">{{ getSortIcon('id') }}</span>
              </th>
              <th class="sortable" @click="sortBy('title')">
                Title <span class="sort-icon">{{ getSortIcon('title') }}</span>
              </th>
              <th class="sortable" @click="sortBy('type')">
                Type <span class="sort-icon">{{ getSortIcon('type') }}</span>
              </th>
              <th class="sortable" @click="sortBy('partner')">
                Partner <span class="sort-icon">{{ getSortIcon('partner') }}</span>
              </th>
              <th class="sortable" @click="sortBy('status')">
                Status <span class="sort-icon">{{ getSortIcon('status') }}</span>
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="agreement in paginatedAgreements"
              :key="agreement.id"
              class="agreement-row"
              @click="openAgreementReview(agreement)"
            >
              <td class="id-cell">{{ agreement.documentCode || agreement.id }}</td>
              <td class="title-cell">{{ agreement.title }}</td>
              <td>{{ agreement.type }}</td>
              <td>{{ agreement.partner }}</td>
              <td>
                <span class="status-badge" :class="getDisplayStatusClass(agreement)">
                  {{ displayStatus(agreement) }}
                </span>
              </td>
              <td class="actions-cell">
                <button class="action-btn view" title="View Details" aria-label="View Details" @click.stop="viewAgreement(agreement)">
                  <CIcon name="cil-description" />
                </button>
                <button v-if="canEditAgreement(agreement)" class="action-btn edit" title="Edit Agreement" aria-label="Edit Agreement" @click.stop="editAgreement(agreement)">
                  <CIcon name="cil-pencil" />
                </button>
              </td>
            </tr>

            <tr v-if="paginatedAgreements.length === 0">
              <td colspan="6" class="empty-state">
                <div class="empty-icon">📄</div>
                <h3>No agreements found</h3>
                <p>Try adjusting your search or filter criteria</p>
                <button class="clear-btn" @click="clearFilters">Clear Filters</button>
              </td>
            </tr>
          </tbody>
        </table>

        <div v-if="filteredAgreements.length > pageSize" class="pagination">
          <div class="pagination-controls">
            <button v-if="currentPage > 1" class="page-btn" @click="currentPage--">
              ← Previous
            </button>
            <span class="page-indicator">Page {{ currentPage }} of {{ totalPages }}</span>
            <button class="page-btn agreements-page-btn agreements-page-btn--next" :disabled="currentPage === totalPages" @click="currentPage++">
              Next →
            </button>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <div v-if="showEditModal" class="modal-overlay" @click.self="closeEditModal">
      <div class="modal-container">
        <div class="modal-header">
          <h2>Edit Agreement</h2>
          <button class="close-btn" @click="closeEditModal">✕</button>
        </div>

        <form class="edit-form" @submit.prevent="saveAgreement">
          <div class="form-group">
            <label>Agreement Title</label>
            <input v-model="editForm.title" type="text" required>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Type</label>
              <select v-model="editForm.type" required>
                <option value="Exchange Student">Exchange Student</option>
                <option value="Extension of MOU">Extension of MOU</option>
                <option value="MOU File">MOU File</option>
                <option value="Double Degree">Double Degree</option>
              </select>
            </div>

            <div class="form-group">
              <label>Partner Institution</label>
              <input v-model="editForm.partner" type="text" required>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Department</label>
              <select v-model="editForm.department" required>
                <option value="International Affairs">International Affairs</option>
                <option value="Research Office">Research Office</option>
                <option value="Student Development">Student Development</option>
                <option value="Academic Services">Academic Services</option>
              </select>
            </div>

            <div class="form-group">
              <label>Status</label>
              <select v-model="editForm.status" required>
                <option value="active">Active</option>
                <option value="pending">Review</option>
                <option value="draft">Draft</option>
                <option value="expiring">Expiring Soon</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Start Date</label>
              <input v-model="editForm.startDate" type="date" required>
            </div>

            <div class="form-group">
              <label>End Date</label>
              <input v-model="editForm.endDate" type="date" required>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" class="cancel-btn" @click="closeEditModal">Cancel</button>
            <button type="submit" class="save-btn">Save Changes</button>
          </div>
        </form>
      </div>
    </div>

    <div v-if="false && showViewModal" class="modal-overlay" @click.self="closeViewModal">
      <div class="modal-container view-modal">
        <div class="modal-header">
          <h2>Agreement Details</h2>
          <button class="close-btn" @click="closeViewModal">✕</button>
        </div>

        <div v-if="selectedAgreement" class="modal-body">
          <div class="view-layout">
            <div class="detail-section">
              <div class="detail-row">
                <span class="detail-label">ID:</span>
                <span class="detail-value">{{ selectedAgreement.documentCode || selectedAgreement.id }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Title:</span>
                <span class="detail-value">{{ selectedAgreement.title }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Type:</span>
                <span class="detail-value">{{ selectedAgreement.type }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Partner:</span>
                <span class="detail-value">{{ selectedAgreement.partner }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Department:</span>
                <span class="detail-value">{{ selectedAgreement.department }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Status:</span>
                <span class="detail-value">
                  <span class="status-badge" :class="getDisplayStatusClass(selectedAgreement)">
                    {{ displayStatus(selectedAgreement) }}
                  </span>
                </span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Created By:</span>
                <span class="detail-value">{{ selectedAgreement.createdBy }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Submitted At:</span>
                <span class="detail-value">{{ formatDate(selectedAgreement.submittedAt) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Current Stage:</span>
                <span class="detail-value">{{ selectedAgreement.currentStage }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Committees:</span>
                <span class="detail-value">{{ formatCommittees(selectedAgreement.committees) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">Start Date:</span>
                <span class="detail-value">{{ formatDate(selectedAgreement.startDate) }}</span>
              </div>
              <div class="detail-row">
                <span class="detail-label">End Date:</span>
                <span class="detail-value">{{ formatDate(selectedAgreement.endDate) }}</span>
              </div>
            </div>

            <div class="agreement-document">
              <div class="agreement-document__header">
                <div class="agreement-document__kicker">Full Agreement</div>
                <div class="agreement-document__title">{{ selectedAgreement.title }}</div>
                <div class="agreement-document__meta">{{ selectedAgreement.type }} · {{ selectedAgreement.partner }}</div>
              </div>

              <div v-if="agreementDocumentSections.length" class="agreement-document__sections">
                <div
                  v-for="section in agreementDocumentSections"
                  :key="section.key"
                  class="agreement-document__section"
                >
                  <div class="agreement-document__section-title">{{ section.title }}</div>
                  <div class="agreement-document__section-content">{{ section.content }}</div>
                </div>
              </div>

              <div v-else class="agreement-document__empty">
                No agreement content is available for this record yet.
              </div>
            </div>
          </div>

          <div class="modal-actions">
            <button v-if="canEditAgreement(selectedAgreement)" class="edit-btn" @click="openEditFromView">✏️ Edit Agreement</button>
            <button class="close-detail-btn" @click="closeViewModal">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AppSectionHero from '@/projects/components/layout/AppSectionHero'
import Service from '@/service/api'
import { readPageCache, writePageCache } from '@/utils/page-cache'
import { isReadyForAdminFinalApproval } from '@/utils/review-final-approval'

const AGREEMENTS_CACHE_KEY = 'admin-agreements-page'
const AGREEMENTS_CACHE_MAX_AGE = 5 * 60 * 1000

function normalizeDepartmentOption(item) {
  if (typeof item === 'string') return item
  if (!item || typeof item !== 'object') return ''
  if (item.name) return String(item.name)
  if (item.value) return String(item.value)
  if (item.label) return String(item.label)
  if (item.code) return String(item.code)
  if (Array.isArray(item.title)) {
    const found = item.title.find(entry => entry && entry.value)
    if (found) return String(found.value)
  }
  return ''
}

function normalizeAgreementWorkflowStatus(item) {
  if (!(item && typeof item === 'object')) return 'draft'
  const status = String(item.status || '').trim().toLowerCase()

  if (status === 'approved') return 'approved'
  if (status === 'rejected') return 'rejected'
  if (isReadyForAdminFinalApproval(item) && status === 'pending') return 'sent-to-admin'
  if (status === 'revision' || status === 'pending') return 'in-review'
  if (status === 'draft') return 'draft'
  return Number(item.currentStep || 1) >= 2 ? 'in-review' : status || 'draft'
}

export default {
  name: 'AdminAgreements',
  components: {
    AppSectionHero
  },
  data() {
    return {
      searchQuery: '',
      departmentFilter: 'all',
      statusFilter: 'all',
      currentPage: 1,
      pageSize: 10,
      sortField: 'id',
      sortDirection: 'asc',
      showEditModal: false,
      showViewModal: false,
      selectedAgreement: null,
      editForm: {
        id: null,
        title: '',
        type: '',
        partner: '',
        department: '',
        status: '',
        startDate: '',
        endDate: ''
      },
      departments: [],
      agreements: []
    }
  },
  computed: {
    heroStats() {
      return [
        {
          label: 'Total Agreements',
          value: this.totalAgreements,
          hint: 'All agreements in system',
          icon: 'cil-description',
          iconClass: 'app-section-stat__icon--total',
          clickable: true,
          active: this.statusFilter === 'all'
        },
        {
          label: 'Active MOUs',
          value: this.activeAgreements,
          hint: 'Currently active',
          icon: 'cil-check-circle',
          iconClass: 'app-section-stat__icon--active',
          clickable: true,
          active: this.statusFilter === 'approved'
        },
        {
          label: 'Review',
          value: this.pendingReview,
          hint: 'Still moving through committee workflow',
          icon: 'cil-hourglass',
          iconClass: 'app-section-stat__icon--attention',
          clickable: true,
          active: this.statusFilter === 'in-review'
        }
      ]
    },
    totalAgreements() {
      return this.agreements.length
    },
    activeAgreements() {
      return this.agreements.filter(item => item.workflowStatus === 'approved').length
    },
    pendingReview() {
      return this.agreements.filter(item => item.workflowStatus === 'in-review').length
    },
    departmentOptions() {
      const source = this.departments.length
        ? this.departments
        : [...new Set(this.agreements.map(item => item.department).filter(Boolean))]
      return source.filter(Boolean).sort((left, right) => String(left).localeCompare(String(right)))
    },
    departmentSelectOptions() {
      return [
        { value: 'all', label: 'All Departments' },
        ...this.departmentOptions.map(item => ({ value: item, label: item }))
      ]
    },
    statusSelectOptions() {
      return [
        { value: 'all', label: 'All Status' },
        { value: 'in-review', label: 'Review' },
        { value: 'sent-to-admin', label: 'Waiting for approval' },
        { value: 'approved', label: 'Approved' },
        { value: 'rejected', label: 'Rejected' },
        { value: 'draft', label: 'Draft' },
        { value: 'revision', label: 'Revision Required' }
      ]
    },
    filteredAgreements() {
      let filtered = [...this.agreements]

      if (this.departmentFilter !== 'all') {
        filtered = filtered.filter(item => item.department === this.departmentFilter)
      }

      if (this.statusFilter !== 'all') {
        filtered = filtered.filter(item => item.workflowStatus === this.statusFilter)
      }

      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase()
        filtered = filtered.filter(item =>
          item.title.toLowerCase().includes(query) ||
          item.partner.toLowerCase().includes(query) ||
          item.department.toLowerCase().includes(query) ||
          item.type.toLowerCase().includes(query) ||
          `mou-${item.id}`.includes(query)
        )
      }

      return this.sortAgreements(filtered)
    },
    paginatedAgreements() {
      const start = (this.currentPage - 1) * this.pageSize
      return this.filteredAgreements.slice(start, start + this.pageSize)
    },
    totalPages() {
      return Math.ceil(this.filteredAgreements.length / this.pageSize)
    },
    paginationStart() {
      return ((this.currentPage - 1) * this.pageSize) + 1
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.pageSize, this.filteredAgreements.length)
    },
    agreementDocumentSections() {
      if (!this.selectedAgreement) return []

      const articleDrafts = Array.isArray(this.selectedAgreement.articleDrafts)
        ? this.selectedAgreement.articleDrafts
        : []

      if (articleDrafts.length) {
        return articleDrafts
          .map((item, index) => ({
            key: String(item && item.key ? item.key : `article-${index + 1}`),
            title: String(item && item.title ? item.title : `Article ${index + 1}`),
            content: String(item && item.content ? item.content : '')
          }))
          .filter((item) => item.content)
      }

      const customDocument = this.selectedAgreement.customDocument
      if (customDocument && customDocument.content) {
        return [{
          key: 'custom-document',
          title: customDocument.title || 'Agreement Content',
          content: customDocument.content
        }]
      }

      if (this.selectedAgreement.summary) {
        return [{
          key: 'summary',
          title: 'Agreement Summary',
          content: this.selectedAgreement.summary
        }]
      }

      return []
    }
  },
  watch: {
    searchQuery() {
      this.currentPage = 1
    },
    departmentFilter() {
      this.currentPage = 1
    },
    statusFilter() {
      this.currentPage = 1
    }
  },
  mounted() {
    this.loadDepartments()
    this.loadCachedAgreements()
    this.loadAgreements()
  },
  methods: {
    normalizeAgreement(item) {
      const stageLabel = item && item.stageLabel
        ? item.stageLabel
        : (isReadyForAdminFinalApproval(item)
          ? 'Waiting for approval'
          : Number(item.currentStep || 1) === 3
            ? 'Committee Review and Comments'
            : Number(item.currentStep || 1) === 2
              ? 'Assign Committee'
              : 'Draft Submitted')
      return {
        ...item,
        workflowStatus: normalizeAgreementWorkflowStatus(item),
        documentCode: item && item.documentCode ? item.documentCode : item && item.id ? `MOU-${item.id}` : '',
        currentStage: stageLabel,
        startDate: item.submittedAt || item.startDate || '',
        endDate: item.approvedAt || item.endDate || ''
      }
    },
    loadCachedAgreements() {
      const cached = readPageCache(AGREEMENTS_CACHE_KEY, AGREEMENTS_CACHE_MAX_AGE)
      if (!Array.isArray(cached) || !cached.length) return
      this.agreements = cached
    },
    async loadAgreements() {
      const response = await Service.adminReview('agreements')
      const records = response && response.data && response.data.data ? response.data.data : []
      const nextAgreements = records.map(this.normalizeAgreement)
      this.agreements = nextAgreements
      writePageCache(AGREEMENTS_CACHE_KEY, nextAgreements)
    },
    async loadDepartments() {
      try {
        const response = await Service.departments('get')
        const rows = Array.isArray(response && response.data && response.data.data)
          ? response.data.data
          : []
        this.departments = rows
          .map(normalizeDepartmentOption)
          .filter(Boolean)
      } catch (error) {
        this.departments = []
      }
    },
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortField = field
        this.sortDirection = 'asc'
      }
    },
    sortAgreements(items) {
      return [...items].sort((left, right) => {
        let leftValue = left[this.sortField]
        let rightValue = right[this.sortField]

        if (this.sortField === 'id') {
          leftValue = Number(leftValue)
          rightValue = Number(rightValue)
        }

        if (leftValue < rightValue) return this.sortDirection === 'asc' ? -1 : 1
        if (leftValue > rightValue) return this.sortDirection === 'asc' ? 1 : -1
        return 0
      })
    },
    getSortIcon(field) {
      if (this.sortField !== field) return '↕️'
      return this.sortDirection === 'asc' ? '↑' : '↓'
    },
    getStatusClass(status) {
      return {
        approved: 'status-active',
        'in-review': 'status-pending',
        'sent-to-admin': 'status-admin',
        rejected: 'status-expiring',
        draft: 'status-draft',
        revision: 'status-expiring',
        expiring: 'status-expiring'
      }[status] || ''
    },
    getDisplayStatusClass(item) {
      if (this.isExpiringSoon(item)) return 'status-expiring'
      return this.getStatusClass(item && item.workflowStatus ? item.workflowStatus : item.status)
    },
    isExpiringSoon(item) {
      const now = new Date()
      const end = new Date(item.endDate)
      const diff = end.getTime() - now.getTime()
      const days = diff / (1000 * 60 * 60 * 24)
      return days >= 0 && days <= 30
    },
    displayStatus(item) {
      if (this.isExpiringSoon(item)) return 'Expiring Soon'
      return this.formatStatus(item && item.workflowStatus ? item.workflowStatus : item.status)
    },
    formatStatus(status) {
      return {
        approved: 'Approved',
        'in-review': 'Review',
        'sent-to-admin': 'Waiting for approval',
        rejected: 'Rejected',
        draft: 'Draft',
        revision: 'Revision Required',
        expiring: 'Expiring Soon'
      }[status] || status
    },
    formatDate(value) {
      if (!value) return '-'
      const date = new Date(value)
      if (Number.isNaN(date.getTime())) return '-'
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    formatCommittees(items) {
      return Array.isArray(items) && items.length ? items.join(', ') : '-'
    },
    clearFilters() {
      this.searchQuery = ''
      this.departmentFilter = 'all'
      this.statusFilter = 'all'
      this.currentPage = 1
    },
    selectStatusCard(stat) {
      const label = String(stat && stat.label || '').toLowerCase()
      const nextStatus = label === 'active mous'
        ? 'approved'
        : label === 'in review'
          ? 'in-review'
          : 'all'
      this.statusFilter = this.statusFilter === nextStatus ? 'all' : nextStatus
    },
    openAgreementReview(agreement) {
      if (!agreement || !agreement.id) return
      this.$router.push({
        path: '/review',
        query: { agreementId: agreement.id }
      })
    },
    viewAgreement(agreement) {
      if (!(agreement && agreement.id)) return
      this.$router.push(`/agreements/${agreement.id}`)
    },
    canEditAgreement(agreement) {
      return !!(agreement && agreement.status !== 'approved')
    },
    editAgreement(agreement) {
      if (!this.canEditAgreement(agreement)) return
      if (agreement.status === 'pending') {
        this.$router.push({
          path: '/review',
          query: { agreementId: agreement.id }
        })
        return
      }
      this.editForm = { ...agreement }
      this.showEditModal = true
    },
    openEditFromView() {
      const agreement = { ...this.selectedAgreement }
      this.showViewModal = false
      this.selectedAgreement = null
      this.editAgreement(agreement)
    },
    closeEditModal() {
      this.showEditModal = false
      this.resetEditForm()
    },
    resetEditForm() {
      this.editForm = {
        id: null,
        title: '',
        type: '',
        partner: '',
        department: '',
        status: '',
        startDate: '',
        endDate: ''
      }
    },
    async saveAgreement() {
      const response = await Service.adminReview('update-agreement', this.editForm)
      const saved = response && response.data && response.data.data ? this.normalizeAgreement(response.data.data) : null
      const index = this.agreements.findIndex(item => item.id === this.editForm.id)
      if (index !== -1 && saved) {
        this.agreements.splice(index, 1, saved)
      }
      this.closeEditModal()
    }
  }
}
</script>

<style scoped lang="scss">
@import "../shared/workspace-consistency.scss";

@include workspace-page-frame("agreements-container");
@include workspace-toolbar-search;
@include workspace-button-system;

.agreements-container {
  margin: 0 auto;
  max-width: 1400px;
  padding: 30px;
}

.filter-shell {
  background: rgba(255, 255, 255, 0.94);
  border: 1px solid #e7edf4;
  border-radius: 18px;
  box-shadow: 0 14px 30px rgba(44, 52, 71, 0.06);
  margin-bottom: 16px;
}

.table-container {
  background: white;
  border: 1px solid #edf2f7;
  border-radius: 16px;
  overflow: hidden;
  padding: 0;
}

.table-header {
  background: #fafbfc;
  border-bottom: 1px solid #edf2f7;
  padding: 16px 20px;
}

.result-count {
  color: #64748b;
  font-size: 13px;
}

.agreements-table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
}

.agreements-table th {
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 16px 20px;
  text-align: left;
  text-transform: uppercase;
}

.agreements-table td {
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  font-size: 14px;
  padding: 16px 20px;
  vertical-align: top;
}

.agreements-table th:nth-child(1),
.agreements-table td:nth-child(1) { width: 20%; }

.agreements-table th:nth-child(2),
.agreements-table td:nth-child(2) { width: 17%; }

.agreements-table th:nth-child(3),
.agreements-table td:nth-child(3) { width: 11%; }

.agreements-table th:nth-child(4),
.agreements-table td:nth-child(4) { width: 12%; }

.agreements-table th:nth-child(5),
.agreements-table td:nth-child(5) { width: 12%; }

.agreements-table th:nth-child(6),
.agreements-table td:nth-child(6) { width: 10%; }

.agreements-table th:nth-child(7),
.agreements-table td:nth-child(7) { width: 8%; }

.agreements-table tbody tr:hover {
  background: #f8fafc;
}

.agreement-row {
  cursor: pointer;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  color: #9E212F;
}

.sort-icon {
  font-size: 12px;
  margin-left: 6px;
  opacity: 0.5;
}

.id-cell {
  color: #9E212F;
  font-weight: 500;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.title-cell {
  color: #1a1a1a;
  font-weight: 500;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.status-badge {
  border-radius: 30px;
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  padding: 6px 12px;
}

.status-active {
  background: #dcfce7;
  color: #166534;
}

.status-pending {
  background: #fff3cd;
  color: #856404;
}

.status-draft {
  background: #e2e3e5;
  color: #383d41;
}

.status-expiring {
  background: #ffe5e5;
  color: #b91c1c;
}

.status-admin {
  background: #e0f2fe;
  color: #2563eb;
}

.actions-cell {
  align-items: center;
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  justify-content: flex-start;
  padding-left: 8px !important;
  padding-right: 12px !important;
  white-space: normal;
}

.agreements-table th:last-child,
.agreements-table td:last-child {
  padding-left: 8px;
  padding-right: 12px;
}

.agreements-table th:nth-child(6),
.agreements-table td:nth-child(6) {
  padding-right: 8px;
}

.action-btn {
  align-items: center;
  border: 0;
  border-radius: 12px;
  cursor: pointer;
  display: inline-flex;
  font-size: 13px;
  font-weight: 700;
  height: 42px;
  justify-content: center;
  margin: 0;
  min-width: 42px;
  padding: 0;
  transition: all 0.2s;
  white-space: nowrap;
}

.action-btn.view {
  background: #e5e7eb;
  border: 1px solid #d1d5db;
  box-shadow: none;
  color: #344767;
}

.action-btn.edit {
  background: #e5e7eb;
  border: 1px solid #d1d5db;
  color: #344767;
}

.action-btn.translate {
  background: #e5e7eb;
  border: 1px solid #d1d5db;
  color: #344767;
}

.action-btn:hover {
  box-shadow: 0 12px 24px rgba(44, 52, 71, 0.12);
  transform: translateY(-1px);
}

.empty-state {
  padding: 60px 20px;
  text-align: center;
}

.empty-icon {
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
}

.empty-state h3 {
  color: #1a1a1a;
  margin-bottom: 8px;
}

.empty-state p {
  color: #64748b;
  margin-bottom: 20px;
}

.clear-btn {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: none;
  color: #475569;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  min-height: 42px;
  padding: 10px 24px;
}

.clear-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.pagination {
  align-items: center;
  background: #fafbfc;
  border-top: 1px solid #edf2f7;
  display: flex;
  justify-content: space-between;
  padding: 20px 20px 28px;
}

.pagination-controls {
  align-items: center;
  display: flex;
  flex: 1;
  gap: 16px;
  justify-content: center;
  position: relative;
}

.pagination-controls .page-btn:first-child {
  left: 18px;
  position: absolute;
}

.pagination-controls .page-btn:last-child {
  position: absolute;
  right: 0;
}

.agreements-page-btn--next {
  background: #9e212f !important;
  border-color: transparent !important;
  box-shadow: 0 10px 20px rgba(158, 33, 47, 0.22) !important;
  color: #fff !important;
}

.agreements-page-btn--next:hover:not(:disabled) {
  background: #9e212f !important;
  box-shadow: 0 10px 20px rgba(158, 33, 47, 0.22) !important;
  transform: none !important;
}

.agreements-page-btn--next:active:not(:disabled) {
  background: #9e212f !important;
  box-shadow: 0 10px 20px rgba(158, 33, 47, 0.22) !important;
  transform: none !important;
}

.agreements-page-btn--next:disabled {
  background: #f7d9dd !important;
  border-color: #f7d9dd !important;
  color: #fff !important;
}

.page-indicator {
  color: #475569;
  font-size: 13px;
  font-weight: 500;
}

.modal-overlay {
  align-items: flex-start;
  backdrop-filter: blur(4px);
  background: rgba(0, 0, 0, 0.5);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1000;
  padding: 40px 24px;
}

.modal-container {
  background: white;
  box-sizing: border-box;
  border-radius: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-height: calc(100vh - 80px);
  max-width: 600px;
  overflow-y: auto;
  width: 90%;
}

.view-modal {
  max-width: 1120px;
}

.modal-header {
  align-items: center;
  border-bottom: 1px solid #edf2f7;
  display: flex;
  justify-content: space-between;
  padding: 20px 24px;
}

.modal-header h2 {
  color: #1a1a1a;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
}

.close-btn {
  align-items: center;
  background: none;
  border: none;
  border-radius: 50%;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  font-size: 20px;
  height: 32px;
  justify-content: center;
  transition: all 0.2s;
  width: 32px;
}

.close-btn:hover {
  background: #f1f5f9;
  color: #9E212F;
}

.modal-body {
  padding: 24px;
}

.view-layout {
  display: grid;
  gap: 20px;
  grid-template-columns: minmax(280px, 360px) minmax(0, 1fr);
}

.edit-form {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  color: #334155;
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
}

.form-group input,
.form-group select {
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  color: #1a1a1a;
  font-size: 14px;
  padding: 12px 16px;
  transition: all 0.2s;
  width: 100%;
}

.form-group input:focus,
.form-group select:focus {
  border-color: #9E212F;
  box-shadow: 0 0 0 3px rgba(158, 33, 47, 0.1);
  outline: none;
}

.form-row {
  display: grid;
  gap: 16px;
  grid-template-columns: 1fr 1fr;
}

.form-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
}

.cancel-btn {
  border-radius: 12px;
  color: #64748b;
  font-size: 14px;
  font-weight: 700;
  min-height: 42px;
  padding: 12px 24px;
}

.save-btn {
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  min-height: 42px;
  padding: 12px 32px;
}

.detail-section {
  background: #fafbfc;
  border: 1px solid #edf2f7;
  border-radius: 18px;
  height: fit-content;
  padding: 18px 20px;
}

.detail-row {
  border-bottom: 1px solid #f1f5f9;
  display: flex;
  padding: 12px 0;
}

.detail-row--stacked {
  align-items: flex-start;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  color: #64748b;
  font-size: 14px;
  font-weight: 500;
  width: 120px;
}

.detail-value {
  color: #1a1a1a;
  flex: 1;
  font-size: 14px;
}

.agreement-document {
  background: linear-gradient(180deg, #ffffff 0%, #fbfcfe 100%);
  border: 1px solid #edf2f7;
  border-radius: 20px;
  min-height: 520px;
  padding: 24px;
}

.agreement-document__header {
  border-bottom: 1px solid #edf2f7;
  margin-bottom: 18px;
  padding-bottom: 18px;
}

.agreement-document__kicker {
  color: #9E212F;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.agreement-document__title {
  color: #1f2937;
  font-size: 1.45rem;
  font-weight: 700;
  margin-top: 8px;
}

.agreement-document__meta {
  color: #64748b;
  margin-top: 8px;
}

.agreement-document__sections {
  display: grid;
  gap: 14px;
}

.agreement-document__section {
  background: #fff;
  border: 1px solid #e7edf4;
  border-radius: 16px;
  padding: 18px;
}

.agreement-document__section-title {
  color: #344767;
  font-size: 0.98rem;
  font-weight: 700;
  margin-bottom: 10px;
}

.agreement-document__section-content {
  color: #334155;
  line-height: 1.75;
  white-space: pre-wrap;
}

.agreement-document__empty {
  color: #64748b;
  padding: 20px 0;
}

.modal-actions {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 20px;
}

.edit-btn {
  align-items: center;
  background: #e5e7eb;
  border: 1px solid #d1d5db;
  border-radius: 12px;
  color: #344767;
  cursor: pointer;
  display: flex;
  font-size: 14px;
  font-weight: 700;
  gap: 6px;
  min-height: 42px;
  padding: 10px 20px;
  transition: all 0.2s;
}

.edit-btn:hover {
  box-shadow: 0 12px 24px rgba(44, 52, 71, 0.12);
  transform: translateY(-1px);
}

.close-detail-btn {
  font-size: 14px;
  min-height: 42px;
  padding: 10px 20px;
}

@media (min-width: 992px) {
  .modal-overlay {
    justify-content: flex-start;
    padding-left: 250px;
    padding-right: 32px;
  }

  .view-modal {
    width: min(1040px, calc(100vw - 300px));
  }
}

@media (max-width: 1024px) {
  .agreements-container {
    padding: 20px;
  }

  .agreements-table {
    min-width: 800px;
  }

  .table-container {
    overflow-x: auto;
  }

  .pagination {
    flex-direction: column;
    gap: 12px;
  }

  .pagination-controls {
    width: 100%;
  }

  .pagination-controls .page-btn:first-child,
  .pagination-controls .page-btn:last-child {
    position: static;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .view-layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .modal-overlay {
    padding: 20px 12px;
  }

  .modal-container {
    max-height: calc(100vh - 40px);
    width: 100%;
  }
}
</style>
